import { MetadataField } from '@/api/generatedApi';
import { DATA_GRID_LAZY_PAGE_SIZE } from '@/components/DataGrid/definitions';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export const LIST_ALL_METADATA_FIELDS_QUERY_KEY = 'metadata.listMetadataFields';

/**
 * @param overrideTenantId resolve fields for a specific tenant instead of the
 * page tenant — needed for cross-tenant search, where the active result tab's
 * tenant (e.g. a remote catalog) differs from the page's home tenant and owns
 * the metadata fields whose TYPES drive the column filter operators.
 */
export const useListAllMetadataFields = (overrideTenantId?: string) => {
  const { getTenantId } = useTenants();
  const tenantId = overrideTenantId ?? getTenantId();
  const queryClient = useQueryClient();

  const {
    data: metadataFields = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: [LIST_ALL_METADATA_FIELDS_QUERY_KEY, tenantId],
    queryFn: async () => {
      const metadataFields: MetadataField[] = [];
      let hasMoreData = true;

      while (hasMoreData) {
        const queryKey = [
          LIST_ALL_METADATA_FIELDS_QUERY_KEY,
          tenantId,
          { page: metadataFields.length / DATA_GRID_LAZY_PAGE_SIZE + 1 },
        ];
        let response = queryClient.getQueryData<Awaited<ReturnType<typeof api.metadata.listMetadataFields>>>(queryKey);
        if (!response) {
          response = await queryClient.fetchQuery({
            queryKey,
            queryFn: () =>
              api.metadata.listMetadataFields({
                tenantId,
                page: metadataFields.length / DATA_GRID_LAZY_PAGE_SIZE + 1,
                perPage: DATA_GRID_LAZY_PAGE_SIZE,
              }),
          });
        }
        metadataFields.push(...(response?.data.metadataFields ?? []));
        hasMoreData = Boolean(response?.data.pageData.total && response.data.pageData.total > metadataFields.length);
      }
      return metadataFields.sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: !!tenantId,
  });

  return {
    metadataFields,
    isLoading,
    error,
  };
};
